/**
 * SkillList Component
 * Vertical sortable list of skills with drag-and-drop reordering
 */

import React from 'react';
import {
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SkillRow } from './SkillRow';
import { useSkillDragDrop } from '../hooks/useSkillDragDrop';
import type { Skill } from '@/types/skills';

interface SkillListProps {
  skills: Skill[];
  onEdit: (skill: Skill) => void;
  onDelete: (skill: Skill) => void;
  onToggle: (skill: Skill) => void;
  onReorder: (skillIds: string[]) => void;
}

export const SkillList: React.FC<SkillListProps> = ({
  skills,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}) => {
  const {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection,
  } = useSkillDragDrop({ onReorder });

  const activeSkill = activeId
    ? skills.find((s) => s.id === activeId)
    : null;

  if (skills.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg mb-2">No skills found</p>
        <p className="text-sm">Create your first skill to get started</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={(event) => handleDragEnd(event, skills)}
      onDragCancel={handleDragCancel}
    >
      <div className="overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[40px_160px_minmax(180px,1fr)_minmax(200px,1.5fr)_80px_100px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div></div>
          <div>ID</div>
          <div>Name / Description</div>
          <div>Command</div>
          <div className="text-center">Status</div>
          <div className="text-right">Actions</div>
        </div>

        {/* Rows */}
        <SortableContext
          items={skills.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="divide-y divide-gray-100">
            {skills.map((skill, index) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                index={index}
                isDragging={isDragging}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggle={onToggle}
              />
            ))}
          </div>
        </SortableContext>
      </div>

      <DragOverlay>
        {activeSkill ? (
          <div className="opacity-90 bg-white shadow-lg rounded-lg border border-primary-300">
            <SkillRow
              skill={activeSkill}
              index={skills.findIndex(s => s.id === activeSkill.id)}
              isDragging={false}
              onEdit={() => {}}
              onDelete={() => {}}
              onToggle={() => {}}
              isOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
